import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import WhiteIconButton from 'components/WhiteIconButton';

import { ProctoringContext } from 'contexts';
import useCaseMasterStore from './CaseMasterStore';
import { CasemasterAssessmentStates } from './constant';

const HEIGHT = '350px';

const sx = {
    container: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: '100%',
        height: '100%',
    },
    videoContainer: {
        overflow: "hidden",
        height: HEIGHT,
        width: HEIGHT,
        borderRadius: '6px',
    },
    videoContainerOff: {
        width: '100%',
        height: '100%',
        backgroundColor: "#353535",
        justifyContent: "center",
        alignItems: "center",
        padding: "1em",
        borderRadius: '6px',
    },
    bufferingImg: {
        position: "absolute",
        width: '100%',
        height: '100%',
        display: "flex",
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        zIndex: -1,
        boxShadow: '0px 0px 3px 0px #00000080'
    }
};

function VideoIcon(props) {
    return (
        <img
            alt="Camera"
            src="https://assets.languify.in/images/camera-off-icon.svg"
            width={48}
            {...props}
        />
    );
}

function BufferingImage() {
    return (
        <Box sx={sx.bufferingImg}>
            <Typography variant="h6-medium" color="neutral.clr-600">
                Buffering...
            </Typography>
        </Box>
    );
}

const CameraPreview = () => {
  const {
    activateImageProctoring, deactivateImageProctoring, onCameraDisable
  } = React.useContext(ProctoringContext);

  const [enabled, setEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  const assessmentState = useCaseMasterStore(state => state.assessmentState);

  const videoRef = useRef();
  const streamRef = React.useRef(null);

  const showComponent = React.useMemo(() => {
    return [
      CasemasterAssessmentStates.SHOWING_TUTORIAL,
      CasemasterAssessmentStates.RUNNING,
    ].includes(assessmentState);
  }, [assessmentState]);

  const toggleProctoring = React.useCallback((videoEnabled) => {
    if (assessmentState === CasemasterAssessmentStates.RUNNING) {
      if (videoEnabled) {
        activateImageProctoring(
            300000, streamRef, videoRef 
        ).catch(console.error);
      } else {
          deactivateImageProctoring(true);
      }
    }
  }, [assessmentState, activateImageProctoring, deactivateImageProctoring]);

    React.useEffect(() => {
      if (!enabled) onCameraDisable();
    }, [enabled, onCameraDisable]);

    React.useEffect(() => {
      if (assessmentState === CasemasterAssessmentStates.RUNNING && enabled && streamRef.current && videoRef.current) {
        console.log('CameraPreview: Activating image proctoring for RUNNING state');
        toggleProctoring(true);
      }
    }, [assessmentState, enabled, toggleProctoring]);

    React.useEffect(() => {
      (async () => {
        if (enabled && showComponent) {
          setLoading(true);
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: true,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            } else {
              streamRef.current?.getTracks()?.forEach((track) => {
                    track.stop();
                });
                setLoading(false);
            }
          } catch (error) {
            console.error(error);
            setEnabled(false);
            setLoading(false);
          }
        } else {
          streamRef.current?.getTracks()?.forEach((track) => {
              track.stop();
          });
          setLoading(false);
        }
        toggleProctoring(enabled);
      })();

      return () => {
          streamRef.current?.getTracks()?.forEach(function (track) {
              track.stop();
          });
          setLoading(false);
      }
    }, [enabled, showComponent]);

  return (
    <>
      <Box
          sx={sx.container}
      >
          <Box
              sx={sx.videoContainer}
              style={{ display: enabled ? "block" : "none" }}
              height={HEIGHT}
          >
              <video
                  key={enabled ? "video-on" : "video-off"}
                  playsInline
                  muted
                  onPlay={() => {
                      console.log('video-playing...');
                      setLoading(false);
                  }}
                  onError={() => { console.log('error') }}
                  ref={videoRef}
                  id="video"
                  style={{
                      display: loading ? "none" : "block",
                      width: `calc(${HEIGHT} * (25/10))`,
                      height: `calc(${HEIGHT} * (25/27))`,
                      marginLeft: `-85%`,
                      marginTop: `-20%`,
                      transform: "rotateY(180deg)",
                      WebkitTransform: "rotateY(180deg)",
                      MozTransform: "rotateY(180deg)",
                  }}
              ></video>
          </Box>
          <Box
              sx={sx.videoContainerOff}
              style={{ display: enabled ? "none" : "flex" }}
          >
              <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
              >
                  <VideoIcon />
                  <Typography variant="body2" color="#CACACA" mb={1}>
                      Camera is off
                  </Typography>
              </Box>
          </Box>
      </Box>

      {loading && <BufferingImage />}

      <WhiteIconButton
          style={{
            position: "absolute",
            bottom: '8px',
            left: '45%',
            transform: 'translateX(-50%)',
            backgroundColor: '#003E8C',
            color: '#FFFFFF',
          }}
          onClick={() => setEnabled(!enabled)}
      >
          {enabled ? <VideocamIcon /> : <VideocamOffIcon />}
      </WhiteIconButton>
    </>
  );
};

export default CameraPreview;